import React, { useEffect, useState } from "react";
import SideNavLayout from "../customsidenav/sidenavlayout";
import api from "../api/careiqapi"; // RPM billing backend lives on the v2 gateway (see RPM_BILLING_HANDOFF.md)
import { useNavigate } from "react-router-dom";
import {
    Backdrop,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Table,
    TableBody,
    TableCell,
    TextField,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Tooltip,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
    LinearProgress,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import SendIcon from "@mui/icons-material/Send";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import ToastMessage from "../utils/mui/righttoptoast";
import { getPracticeId, hasMultipleDepartments } from "../config/practice";

const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const billedMonthSet = (monthEntries) => {
    const s = new Set();
    if (!monthEntries) return s;
    monthEntries.forEach((e) => {
        if (e.month) s.add(e.month);
    });
    return s;
};

const dosForMonth = (monthEntries, monthKey) => {
    if (!monthEntries) return null;
    const entry = monthEntries.find((e) => e.month === monthKey);
    return entry ? entry.dos : null;
};

const isWithinMonths = (dateStr, months) => {
    if (!dateStr) return false;

    let d;
    const s = String(dateStr).trim();
    if (s.includes("T")) {
        d = new Date(s);
    } else if (s.includes("/")) {
        const [m, day, y] = s.split("/");
        d = new Date(parseInt(y, 10), parseInt(m, 10) - 1, parseInt(day, 10));
    } else if (s.includes("-")) {
        const parts = s.split("-");
        d = new Date(parseInt(parts[0], 10), parseInt(parts[1], 10) - 1, parseInt(parts[2] || 1, 10));
    } else {
        return false;
    }

    if (Number.isNaN(d.getTime())) return false;
    const cutoff = new Date();
    cutoff.setMonth(cutoff.getMonth() - months);
    return d >= cutoff;
};

const formatServiceDate = (dateStr) => {
    if (!dateStr) return "";
    const s = String(dateStr).trim();
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(s)) return s;
    if (s.includes("T")) {
        const d = new Date(s);
        return `${String(d.getMonth() + 1).padStart(2, "0")}/${String(d.getDate()).padStart(2, "0")}/${d.getFullYear()}`;
    }
    if (s.includes("-")) {
        const [y, m, d] = s.split("-");
        return `${m.padStart(2, "0")}/${(d || "01").padStart(2, "0")}/${y}`;
    }
    return s;
};

const getLast12Months = () => {
    const now = new Date();
    const months = [];
    for (let i = 11; i >= 0; i -= 1) {
        const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
        const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
        const label = MONTH_NAMES[d.getMonth()];
        const shortLabel = label.charAt(0);
        months.push({ key, label, shortLabel });
    }
    return months;
};

const LAST_12 = getLast12Months();
const PARTIAL_BILLING_START_MONTH = "2026-01";
const CURRENT_MONTH_KEY = LAST_12[LAST_12.length - 1].key;
const EARLIEST_MONTH_KEY = LAST_12[0].key;

const monthKeyWithOffset = (offset) => {
    const d = new Date();
    d.setDate(1);
    d.setMonth(d.getMonth() + offset);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};
// Safe default billing window: last 6 completed months. Guards against
// accidentally submitting claims for old service months.
const DEFAULT_FROM_MONTH = monthKeyWithOffset(-6);
const DEFAULT_TO_MONTH = monthKeyWithOffset(-1);

const monthKeyOfDate = (dateStr) => {
    if (!dateStr) return null;
    const s = String(dateStr).trim();
    if (/^\d{4}-\d{2}/.test(s)) return s.slice(0, 7);
    const m = s.match(/^(\d{1,2})\/\d{1,2}\/(\d{4})/);
    if (m) return `${m[2]}-${m[1].padStart(2, "0")}`;
    return null;
};
const getRowKey = (patient) => patient?.rowkey || `${patient?.sourcepracticeid || "local"}:${patient?.patientid}`;
const getDisplayDepartment = (patient) => patient?.departmentid || patient?.billingdepartmentid || 1;
const getSourceLabel = (patient) => {
    if (patient?.sourcepracticeid === 1111) return "Dept 2 Mirror";
    return `Dept ${getDisplayDepartment(patient)}`;
};

const BillingDots = ({ monthEntries, readingDays = {}, eligibilityBand = "full", windowFrom = null, windowTo = null }) => {
    const billedSet = billedMonthSet(monthEntries);
    return (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
            <div style={{ display: "flex", gap: "3px" }}>
                {LAST_12.map((m) => {
                    const isBilled = billedSet.has(m.key);
                    const dos = dosForMonth(monthEntries, m.key);
                    const days = readingDays[m.key] || 0;
                    const isCurrent = m.key === CURRENT_MONTH_KEY;
                    const outOfWindow = (windowFrom && m.key < windowFrom) || (windowTo && m.key > windowTo && !isCurrent);
                    const isEligible = !isBilled && !isCurrent && (
                        eligibilityBand === "partial"
                            ? (m.key >= PARTIAL_BILLING_START_MONTH && days >= 2 && days <= 15)
                            : days >= 16
                    );

                    let bg;
                    let border;
                    let tooltipText;
                    let dotContent;

                    if (isBilled) {
                        bg = "#4caf50";
                        border = "2px solid #388e3c";
                        tooltipText = `Billed: ${dos}`;
                        dotContent = null;
                    } else if (isCurrent) {
                        bg = "#e3f2fd";
                        border = "2px dashed #1976d2";
                        tooltipText = `${m.label} — In progress (current month, not yet billable)`;
                        dotContent = (
                            <span style={{ fontSize: "8px", fontWeight: 700, color: "#1976d2", lineHeight: "10px" }}>
                                P
                            </span>
                        );
                    } else if (isEligible) {
                        bg = "#ff9800";
                        border = "2px solid #e65100";
                        tooltipText = `${m.label} — Eligible (${days} reading days)`;
                        dotContent = (
                            <span style={{ fontSize: "8px", fontWeight: 700, color: "#fff", lineHeight: "10px" }}>
                                E
                            </span>
                        );
                    } else {
                        bg = "#e0e0e0";
                        border = "2px solid #bdbdbd";
                        if (eligibilityBand === "partial") {
                            if (m.key < PARTIAL_BILLING_START_MONTH) {
                                tooltipText = `${m.label} — 99445/99470 not eligible before Jan 2026`;
                            } else {
                                tooltipText = days > 0 ? `${m.label} — ${days} reading days (need 2-15)` : `${m.label} — no readings`;
                            }
                        } else {
                            tooltipText = days > 0 ? `${m.label} — ${days} reading days (need 16+)` : `${m.label} — no readings`;
                        }
                        dotContent = null;
                    }

                    if (outOfWindow && !isBilled) {
                        tooltipText += " — outside billing window";
                    }

                    return (
                        <Tooltip key={m.key} title={tooltipText} arrow placement="top">
                            <div
                                style={{
                                    width: 14,
                                    height: 14,
                                    borderRadius: "50%",
                                    backgroundColor: bg,
                                    border,
                                    opacity: outOfWindow && !isBilled ? 0.35 : 1,
                                    cursor: "default",
                                    transition: "transform 0.15s",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.transform = "scale(1.3)";
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.transform = "scale(1)";
                                }}
                            >
                                {dotContent}
                            </div>
                        </Tooltip>
                    );
                })}
            </div>
            <div style={{ display: "flex", gap: "3px", marginTop: "1px" }}>
                {LAST_12.map((m) => (
                    <span key={m.key} style={{ width: 14, textAlign: "center", fontSize: "8px", color: "#9e9e9e", lineHeight: 1 }}>
                        {m.shortLabel}
                    </span>
                ))}
            </div>
        </div>
    );
};

const SetupDot = ({ claimInfo }) => {
    const billed = claimInfo?.billed;
    return (
        <Tooltip title={billed ? `Billed: ${claimInfo.dos}` : "Not billed"} arrow placement="top">
            <Chip
                label={billed ? "✅" : "—"}
                size="small"
                sx={{
                    backgroundColor: billed ? "#e8f5e9" : "#f5f5f5",
                    color: billed ? "#2e7d32" : "#9e9e9e",
                    fontWeight: 600,
                    fontSize: "11px",
                    height: "22px",
                }}
            />
        </Tooltip>
    );
};

const StatusIcon = ({ status, error }) => {
    switch (status) {
        case "pending":
            return <Chip label="Pending" size="small" variant="outlined" sx={{ fontSize: "11px" }} />;
        case "submitting":
            return <CircularProgress size={16} sx={{ color: "#50869A" }} />;
        case "success":
            return (
                <Tooltip title="Submitted successfully" arrow>
                    <CheckCircleIcon sx={{ color: "#4caf50", fontSize: 20 }} />
                </Tooltip>
            );
        case "skipped":
            return (
                <Tooltip title={error || "Skipped — already billed"} arrow>
                    <WarningAmberIcon sx={{ color: "#ff9800", fontSize: 20 }} />
                </Tooltip>
            );
        case "timeout":
            return (
                <Tooltip title={error || "Timeout — verify in Athena before retrying"} arrow>
                    <WarningAmberIcon sx={{ color: "#e65100", fontSize: 20 }} />
                </Tooltip>
            );
        case "failed":
            return (
                <Tooltip title={error || "Submission failed"} arrow>
                    <ErrorIcon sx={{ color: "#d32f2f", fontSize: 20 }} />
                </Tooltip>
            );
        default:
            return <HourglassEmptyIcon sx={{ color: "#bdbdbd", fontSize: 18 }} />;
    }
};

const RpmBilling = () => {
    const practiseid = getPracticeId();
    const navigate = useNavigate();

    const [patients, setPatients] = useState([]);
    const [providers, setProviders] = useState([]);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const [selectedProvider, setSelectedProvider] = useState("");
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [filter, setFilter] = useState("all");
    const [deptFilter, setDeptFilter] = useState("all");
    const [fromMonth, setFromMonth] = useState(DEFAULT_FROM_MONTH);
    const [toMonth, setToMonth] = useState(DEFAULT_TO_MONTH);

    const [confirmOpen, setConfirmOpen] = useState(false);
    const [claimsPreview, setClaimsPreview] = useState([]);
    const [submitProgress, setSubmitProgress] = useState(0);
    const [billingMode, setBillingMode] = useState("99453");
    const [previewLoading, setPreviewLoading] = useState(false);
    const [driftWarnings, setDriftWarnings] = useState([]);
    const [backendNotes, setBackendNotes] = useState([]);

    const [drillOpen, setDrillOpen] = useState(false);
    const [drillPatient, setDrillPatient] = useState(null);
    const [drillData, setDrillData] = useState(null);
    const [drillLoading, setDrillLoading] = useState(false);
    const [expandedMonth, setExpandedMonth] = useState(null);

    const [openToast, setOpenToast] = useState(false);
    const [toastMessage, setToastMessage] = useState("");
    const [toastSeverity, setToastSeverity] = useState("success");

    const fetchBillingData = async () => {
        setLoading(true);
        try {
            const res = await api.get(`/v1/${practiseid}/rpm/billing`);
            const data = res.data || res;
            if (data.status === 200) {
                setPatients(data.patients || []);
                setProviders(data.providers || []);
            } else {
                setPatients([]);
                setProviders([]);
            }
        } catch (err) {
            console.error(err);
            setPatients([]);
            setProviders([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchBillingData();
    }, []);

    // Billing window (inclusive YYYY-MM). Everything — tab membership, counts,
    // preview and submit — respects it, and the backend enforces it again.
    const windowFrom = fromMonth || EARLIEST_MONTH_KEY;
    const windowTo = toMonth || DEFAULT_TO_MONTH;
    const monthInWindow = (key) => key >= windowFrom && key <= windowTo;

    const is99453NotBilled = (p) => p.billing_ready !== false && !p.claims?.["99453"]?.billed;
    const isEligible = (p) => isWithinMonths(p.first_bp_date, 12);
    // 99453's DOS is the first reading date — hide setups older than the
    // window so an old DOS can't be submitted by accident (a setup done
    // this month is still fine).
    const setupInWindow = (p) => {
        const mk = monthKeyOfDate(p.first_bp_date);
        return !mk || mk >= windowFrom;
    };

    // Mirror rows without an Athena identity can't be billed — the backend
    // returns them as blocked. Keep them visible but out of the pending tabs.
    const isBlocked = (p) => p.billing_ready === false;
    const monthsBilledIn = (p, codes) => new Set(codes.flatMap((c) => (p.claims?.[c] || []).map((e) => e.month)));

    // Bands are mutually exclusive per month: a month billed under the other
    // band is NOT pending (backend refuses it too — needs a claim correction).
    const has99454Pending = (p) => {
        if (isBlocked(p)) return false;
        const billed454 = new Set((p.claims?.["99454"] || []).map((e) => e.month));
        const billed457 = new Set((p.claims?.["99457"] || []).map((e) => e.month));
        const billedPartial = monthsBilledIn(p, ["99445", "99470"]);
        const readingDays = p.reading_days || {};
        return LAST_12.some((m) => m.key !== CURRENT_MONTH_KEY && monthInWindow(m.key) && (readingDays[m.key] || 0) >= 16 && !billedPartial.has(m.key) && !(billed454.has(m.key) && billed457.has(m.key)));
    };

    const has99445Pending = (p) => {
        if (isBlocked(p)) return false;
        const billed445 = new Set((p.claims?.["99445"] || []).map((e) => e.month));
        const billed470 = new Set((p.claims?.["99470"] || []).map((e) => e.month));
        const billedFull = monthsBilledIn(p, ["99454", "99457"]);
        const readingDays = p.reading_days || {};
        return LAST_12.some((m) => {
            const days = readingDays[m.key] || 0;
            return m.key !== CURRENT_MONTH_KEY && monthInWindow(m.key) && m.key >= PARTIAL_BILLING_START_MONTH && days >= 2 && days <= 15 && !billedFull.has(m.key) && !(billed445.has(m.key) && billed470.has(m.key));
        });
    };

    const deptPatients =
        deptFilter === "all"
            ? patients
            : patients.filter((p) => String(getDisplayDepartment(p)) === deptFilter);

    const filteredPatients =
        filter === "not_billed"
            ? deptPatients.filter((p) => is99453NotBilled(p) && setupInWindow(p))
            : filter === "monthly_pending"
              ? deptPatients.filter((p) => has99454Pending(p))
              : filter === "partial_pending"
                ? deptPatients.filter((p) => has99445Pending(p))
              : deptPatients;

    const billedCount = deptPatients.filter((p) => p.claims?.["99453"]?.billed).length;
    const unbilledCount = deptPatients.filter((p) => is99453NotBilled(p) && setupInWindow(p)).length;
    const monthlyPendingCount = deptPatients.filter((p) => has99454Pending(p)).length;
    const partialPendingCount = deptPatients.filter((p) => has99445Pending(p)).length;
    const blockedCount = deptPatients.filter((p) => p.billing_ready === false).length;

    // Filters changed → drop selections that are no longer visible, so a
    // hidden row can never ride along into a submit.
    useEffect(() => {
        setSelectedIds((prev) => {
            if (prev.size === 0) return prev;
            const visible = new Set(filteredPatients.map((p) => getRowKey(p)));
            const next = new Set(Array.from(prev).filter((k) => visible.has(k)));
            return next.size === prev.size ? prev : next;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [deptFilter, fromMonth, toMonth, filter, patients]);

    const toggleSelect = (rowKey) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(rowKey)) next.delete(rowKey);
            else next.add(rowKey);
            return next;
        });
    };

    const selectablePatients = filteredPatients;

    const toggleSelectAll = () => {
        if (selectedIds.size === selectablePatients.length && selectablePatients.length > 0) {
            setSelectedIds(new Set());
        } else {
            setSelectedIds(new Set(selectablePatients.map((p) => getRowKey(p))));
        }
    };

    const getProviderName = (id) => {
        const p = providers.find((pr) => pr.providerid === id);
        return p ? p.providername : id;
    };

    const handlePatientClick = async (patient) => {
        setDrillPatient(patient);
        setDrillData(null);
        setDrillLoading(true);
        setDrillOpen(true);
        setExpandedMonth(null);

        try {
            const res = await api.get(`/v1/${practiseid}/rpm/billing/patient/${patient.patientid}/readings`, {
                params: { sourcepracticeid: patient.sourcepracticeid || practiseid },
            });
            const data = res.data || res;
            setDrillData(data);
        } catch (err) {
            console.error("Failed to fetch readings:", err);
            setDrillData({ months: [], message: "Error loading readings" });
        } finally {
            setDrillLoading(false);
        }
    };

    const validateSelection = () => {
        if (!selectedProvider) {
            setToastMessage("Please select a supervising provider");
            setToastSeverity("warning");
            setOpenToast(true);
            return false;
        }
        if (selectedIds.size === 0) {
            setToastMessage("Please select at least one patient");
            setToastSeverity("warning");
            setOpenToast(true);
            return false;
        }
        return true;
    };

    const handlePreSubmit = async () => {
        if (!validateSelection()) return;

        setBillingMode("99453");
        const preview = Array.from(selectedIds).map((rowKey) => {
            const p = patients.find((pt) => getRowKey(pt) === rowKey);
            return {
                rowkey: rowKey,
                patientid: p.patientid,
                name: `${p.firstname} ${p.lastname}`,
                dob: p.dob,
                dos: formatServiceDate(p.first_bp_date),
                departmentid: parseInt(p.departmentid, 10) || 1,
                sourcepracticeid: parseInt(p.sourcepracticeid || practiseid, 10),
                billingpracticeid: parseInt(p.billingpracticeid || practiseid, 10),
                billingdepartmentid: parseInt(p.billingdepartmentid || p.departmentid, 10) || 1,
                sourceLabel: getSourceLabel(p),
                cpt: "99453",
                icd: "I10",
                providerid: selectedProvider,
                provider: getProviderName(selectedProvider),
                status: "pending",
                insuranceUsed: "Resolving...",
                error: null,
            };
        });

        setClaimsPreview(preview);
        setSubmitProgress(0);
        setConfirmOpen(true);

        try {
            const previewPayload = {
                patients: preview.map((c) => ({
                    rowkey: c.rowkey,
                    patientid: c.patientid,
                    departmentid: c.departmentid,
                    sourcepracticeid: c.sourcepracticeid,
                    billingpracticeid: c.billingpracticeid,
                    billingdepartmentid: c.billingdepartmentid,
                    servicedate: c.dos,
                })),
            };
            const res = await api.post(`/v1/${practiseid}/rpm/billing/preview`, previewPayload);
            const data = res.data || res;
            if (data.results) {
                const insMap = {};
                data.results.forEach((r) => {
                    insMap[r.rowkey || `${r.sourcepracticeid || practiseid}:${r.patientid}`] = r.insurance_info;
                });
                setClaimsPreview((prev) => prev.map((c) => ({ ...c, insuranceUsed: insMap[c.rowkey] || "Not found" })));
            }
        } catch (err) {
            console.error("Insurance preview failed:", err);
            setClaimsPreview((prev) => prev.map((c) => ({ ...c, insuranceUsed: "Lookup failed" })));
        }
    };

    const handlePreSubmitMonthly = async () => {
        if (!validateSelection()) return;

        setBillingMode("monthly");
        setClaimsPreview([]);
        setDriftWarnings([]);
        setBackendNotes([]);
        setSubmitProgress(0);
        setConfirmOpen(true);
        setPreviewLoading(true);

        const selectedPatients = Array.from(selectedIds).map((rowKey) => patients.find((pt) => getRowKey(pt) === rowKey));
        const patientMeta = Object.fromEntries(selectedPatients.map((p) => [getRowKey(p), {
            name: `${p.firstname} ${p.lastname}`,
            dob: p.dob,
            departmentid: parseInt(p.departmentid, 10) || 1,
            sourcepracticeid: parseInt(p.sourcepracticeid || practiseid, 10),
            billingpracticeid: parseInt(p.billingpracticeid || practiseid, 10),
            billingdepartmentid: parseInt(p.billingdepartmentid || p.departmentid, 10) || 1,
            sourceLabel: getSourceLabel(p),
        }]));

        const frontendCounts = {};
        selectedPatients.forEach((p) => {
            const readingDays = p.reading_days || {};
            const billed454 = new Set((p.claims?.["99454"] || []).map((e) => e.month));
            const billed457 = new Set((p.claims?.["99457"] || []).map((e) => e.month));
            const billed445 = new Set((p.claims?.["99445"] || []).map((e) => e.month));
            const billed470 = new Set((p.claims?.["99470"] || []).map((e) => e.month));
            // Same rules as the backend: nothing for a blocked row, never a month
            // already billed under the other band, only the codes still missing.
            let expected = 0;
            if (!isBlocked(p)) {
                LAST_12.forEach((m) => {
                    if (m.key === CURRENT_MONTH_KEY || !monthInWindow(m.key)) return;
                    const days = readingDays[m.key] || 0;
                    const partialBilled = billed445.has(m.key) || billed470.has(m.key);
                    const fullBilled = billed454.has(m.key) || billed457.has(m.key);
                    if (days >= 16 && !partialBilled) {
                        expected += (billed454.has(m.key) ? 0 : 1) + (billed457.has(m.key) ? 0 : 1);
                    } else if (days >= 2 && days <= 15 && m.key >= PARTIAL_BILLING_START_MONTH && !fullBilled) {
                        expected += (billed445.has(m.key) ? 0 : 1) + (billed470.has(m.key) ? 0 : 1);
                    }
                });
            }
            frontendCounts[getRowKey(p)] = expected;
        });

        const previewPayload = {
            supervisingproviderid: selectedProvider,
                from_month: windowFrom,
                to_month: windowTo,
                patients: selectedPatients.map((p) => ({
                    rowkey: getRowKey(p),
                    patientid: p.patientid,
                    departmentid: parseInt(p.departmentid, 10) || 1,
                    sourcepracticeid: parseInt(p.sourcepracticeid || practiseid, 10),
                    billingpracticeid: parseInt(p.billingpracticeid || practiseid, 10),
                    billingdepartmentid: parseInt(p.billingdepartmentid || p.departmentid, 10) || 1,
                    servicedate: new Date().toISOString().split("T")[0],
                })),
            };

        try {
            const res = await api.post(`/v1/${practiseid}/rpm/billing/preview-monthly`, previewPayload);
            const data = res.data || res;
            const rows = (data.results || []).map((r) => {
                const rowKey = r.rowkey || `${r.sourcepracticeid || practiseid}:${r.patientid}`;
                const meta = patientMeta[rowKey] || {};
                return {
                    rowkey: rowKey,
                    patientid: r.patientid,
                    sourcepracticeid: meta.sourcepracticeid || r.sourcepracticeid || practiseid,
                    billingpracticeid: meta.billingpracticeid || r.billingpracticeid || practiseid,
                    billingdepartmentid: meta.billingdepartmentid || r.billingdepartmentid || meta.departmentid || 1,
                    sourceLabel: meta.sourceLabel || `Dept ${meta.departmentid || 1}`,
                    name: meta.name || String(r.patientid),
                    dob: meta.dob || "",
                    departmentid: meta.departmentid || 1,
                    month: r.month,
                    cpt: r.cpt,
                    band: r.band,
                    days: r.days,
                    icd: "I10",
                    providerid: selectedProvider,
                    provider: getProviderName(selectedProvider),
                    status: "pending",
                    insuranceUsed: "—",
                    error: null,
                };
            });
            setClaimsPreview(rows);

            const backendCounts = {};
            rows.forEach((r) => { backendCounts[r.rowkey] = (backendCounts[r.rowkey] || 0) + 1; });
            const drift = selectedPatients
                .map((p) => ({
                    rowkey: getRowKey(p),
                    patientid: p.patientid,
                    name: `${p.firstname} ${p.lastname}`,
                    frontend: frontendCounts[getRowKey(p)] || 0,
                    backend: backendCounts[getRowKey(p)] || 0,
                }))
                .filter((d) => d.frontend !== d.backend);
            setDriftWarnings(drift);
            const nameOf = (rk, pid) => patientMeta[rk]?.name || String(pid);
            setBackendNotes([
                ...(data.blocked_patients || []).map((b) => ({
                    key: `b:${b.rowkey}`,
                    text: `${nameOf(b.rowkey, b.patientid)} — BLOCKED: ${b.error}`,
                })),
                ...(data.needs_correction || []).map((n) => ({
                    key: `c:${n.rowkey}:${n.month}`,
                    text: `${nameOf(n.rowkey, n.patientid)} ${n.month} — ${n.error} (already billed: ${(n.already_billed || []).join("+")})`,
                })),
            ]);
        } catch (err) {
            console.error("Preview-monthly failed:", err);
            setToastMessage("Could not load billing preview — try again");
            setToastSeverity("error");
            setOpenToast(true);
            setConfirmOpen(false);
        } finally {
            setPreviewLoading(false);
        }
    };

    const handleConfirmedSubmit = async () => {
        setSubmitting(true);
        let successCount = 0;
        let failCount = 0;

        for (let i = 0; i < claimsPreview.length; i += 1) {
            setClaimsPreview((prev) => prev.map((c, idx) => (idx === i ? { ...c, status: "submitting" } : c)));

            try {
                const claim = claimsPreview[i];
                const payload = {
                    supervisingproviderid: claim.providerid,
                    patients: [{
                        rowkey: claim.rowkey,
                        patientid: claim.patientid,
                        departmentid: claim.departmentid,
                        sourcepracticeid: claim.sourcepracticeid,
                        billingpracticeid: claim.billingpracticeid,
                        billingdepartmentid: claim.billingdepartmentid,
                        servicedate: claim.dos,
                    }],
                };

                const res = await api.post(`/v1/${practiseid}/rpm/billing/submit`, payload);
                const data = res.data || res;

                if (data.fail_count > 0) {
                    throw new Error(data.results?.[0]?.error || "Submission failed");
                }

                const result = data.results?.[0] || {};
                const insInfo = result.insurance_info || "";
                let resultStatus = "success";
                if (result.status === "skipped") resultStatus = "skipped";
                if (result.status === "timeout") resultStatus = "timeout";
                const resultError = result.status === "skipped" || result.status === "timeout" ? result.error : null;

                setClaimsPreview((prev) =>
                    prev.map((c, idx) => (
                        idx === i ? { ...c, status: resultStatus, insuranceUsed: insInfo || c.insuranceUsed, error: resultError } : c
                    ))
                );
                if (resultStatus === "success") successCount += 1;
            } catch (err) {
                const errMsg = err?.response?.data?.message || err.message || "Unknown error";
                setClaimsPreview((prev) => prev.map((c, idx) => (idx === i ? { ...c, status: "failed", error: errMsg } : c)));
                failCount += 1;
            }

            setSubmitProgress(i + 1);
        }

        setSubmitting(false);
        setToastMessage(`Done: ${successCount} succeeded, ${failCount} failed`);
        setToastSeverity(failCount === 0 ? "success" : "warning");
        setOpenToast(true);
        setSelectedIds(new Set());
        setTimeout(() => fetchBillingData(), 1500);
    };

    const handleConfirmedSubmitMonthly = async () => {
        setSubmitting(true);
        let successCount = 0;
        let failCount = 0;

        const patientKeys = Array.from(new Set(claimsPreview.map((c) => c.rowkey)));

        for (let pIdx = 0; pIdx < patientKeys.length; pIdx += 1) {
            const rowKey = patientKeys[pIdx];
            setClaimsPreview((prev) => prev.map((c) => (c.rowkey === rowKey ? { ...c, status: "submitting" } : c)));

            try {
                const firstRow = claimsPreview.find((c) => c.rowkey === rowKey);
                const patient = patients.find((p) => getRowKey(p) === rowKey);
                const payload = {
                    supervisingproviderid: firstRow.providerid,
                    from_month: windowFrom,
                    to_month: windowTo,
                    patients: [{
                        rowkey: firstRow.rowkey,
                        patientid: firstRow.patientid,
                        departmentid: firstRow.departmentid,
                        sourcepracticeid: firstRow.sourcepracticeid,
                        billingpracticeid: firstRow.billingpracticeid,
                        billingdepartmentid: firstRow.billingdepartmentid,
                        servicedate: formatServiceDate(patient?.first_bp_date) || "",
                    }],
                };

                const res = await api.post(`/v1/${practiseid}/rpm/billing/submit-monthly`, payload);
                const data = res.data || res;
                const r = (data.results || [])[0];
                if (!r) throw new Error("No response from server");

                const insInfo = r.insurance_info || "";
                const monthResults = r.months_billed || [];

                setClaimsPreview((prev) => prev.map((c) => {
                    if (c.rowkey !== rowKey) return c;
                    const match = monthResults.find((m) => m.month === c.month && String(m.cpt) === String(c.cpt));
                    if (!match) {
                        return { ...c, status: "skipped", error: r.error || "Not in backend response", insuranceUsed: insInfo || c.insuranceUsed };
                    }
                    return {
                        ...c,
                        status: match.status,
                        error: match.error || null,
                        insuranceUsed: insInfo || c.insuranceUsed,
                    };
                }));

                const sCount = monthResults.filter((m) => m.status === "success").length;
                const fCount = monthResults.filter((m) => m.status === "failed").length;
                if (fCount > 0) failCount += 1;
                else if (sCount > 0) successCount += 1;
            } catch (err) {
                const errMsg = err?.response?.data?.message || err.message || "Unknown error";
                setClaimsPreview((prev) => prev.map((c) => (c.rowkey === rowKey ? { ...c, status: "failed", error: errMsg } : c)));
                failCount += 1;
            }

            setSubmitProgress(pIdx + 1);
        }

        setSubmitting(false);
        setToastMessage(`Monthly billing: ${successCount} patients succeeded, ${failCount} failed`);
        setToastSeverity(failCount === 0 ? "success" : "warning");
        setOpenToast(true);
        setSelectedIds(new Set());
        setTimeout(() => fetchBillingData(), 1500);
    };

    const isSubmitDone =
        claimsPreview.length > 0 && claimsPreview.every((c) => ["success", "failed", "skipped", "timeout"].includes(c.status));

    return (
        <div className="h-full">
            <SideNavLayout>
                <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
                    <div className="flex items-center gap-3">
                        <button
                            onClick={() => navigate("/rpm")}
                            className="text-[black] font-semibold text-[12px] h-8 px-2 rounded hover:bg-[#D97842] hover:text-white"
                        >
                            <ArrowBackIcon style={{ width: "15px" }} /> RPM
                        </button>
                        <div className="flex items-center gap-2">
                            <ReceiptLongIcon style={{ color: "#50869A" }} />
                            <span className="text-lg font-semibold text-[#1d2951]">RPM Billing Dashboard</span>
                        </div>
                    </div>
                    <div className="flex gap-2 items-center">
                        <Chip label={`Total: ${deptPatients.length}`} variant="outlined" size="small" />
                        <Chip label={`99453 Billed: ${billedCount}`} size="small" sx={{ backgroundColor: "#4caf50", color: "#fff" }} />
                        <Chip label={`99453 Pending: ${unbilledCount}`} size="small" sx={{ backgroundColor: "#ff9800", color: "#fff" }} />
                        <Chip label={`99454/99457 Pending: ${monthlyPendingCount}`} size="small" sx={{ backgroundColor: "#7b1fa2", color: "#fff" }} />
                        <Chip label={`99445/99470 Pending: ${partialPendingCount}`} size="small" sx={{ backgroundColor: "#1565c0", color: "#fff" }} />
                        {blockedCount > 0 && (
                            <Tooltip title="Legacy (1111) RPM rows with no Athena patient mapping — cannot be billed until the patient is linked in Onboarding > Unmatched patient mapping" arrow>
                                <Chip label={`Blocked (no Athena ID): ${blockedCount}`} size="small" sx={{ backgroundColor: "#d32f2f", color: "#fff" }} />
                            </Tooltip>
                        )}
                    </div>
                </div>

                <div className="flex items-center flex-wrap gap-3 mb-3">
                    <FormControl size="small" sx={{ minWidth: 250 }}>
                        <InputLabel id="provider-label">Supervising Provider</InputLabel>
                        <Select
                            labelId="provider-label"
                            value={selectedProvider}
                            label="Supervising Provider"
                            onChange={(e) => setSelectedProvider(e.target.value)}
                        >
                            {providers.map((prov) => (
                                <MenuItem key={prov.providerid} value={prov.providerid}>
                                    {prov.providername}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {/* 9928's two locations; a one-location practice has nothing to filter. */}
                    {hasMultipleDepartments() && <FormControl size="small" sx={{ minWidth: 160 }}>
                        <InputLabel id="dept-filter-label">Department</InputLabel>
                        <Select
                            labelId="dept-filter-label"
                            value={deptFilter}
                            label="Department"
                            onChange={(e) => setDeptFilter(e.target.value)}
                        >
                            <MenuItem value="all">All departments</MenuItem>
                            <MenuItem value="1">Dept 1 — Odessa</MenuItem>
                            <MenuItem value="2">Dept 2 — Dallas</MenuItem>
                        </Select>
                    </FormControl>}

                    <TextField
                        label="From month"
                        type="month"
                        size="small"
                        value={fromMonth}
                        onChange={(e) => setFromMonth(e.target.value)}
                        inputProps={{ min: EARLIEST_MONTH_KEY, max: windowTo }}
                        InputLabelProps={{ shrink: true }}
                        sx={{ width: 160 }}
                    />
                    <TextField
                        label="To month"
                        type="month"
                        size="small"
                        value={toMonth}
                        onChange={(e) => setToMonth(e.target.value)}
                        inputProps={{ min: windowFrom, max: DEFAULT_TO_MONTH }}
                        InputLabelProps={{ shrink: true }}
                        sx={{ width: 160 }}
                    />
                    <Tooltip title="Only service months inside this window are listed, previewed and submitted (enforced server-side too). Widen it deliberately to bill older months." arrow>
                        <Chip
                            label={`Billing window: ${windowFrom} → ${windowTo}`}
                            size="small"
                            variant="outlined"
                            sx={{ borderColor: "#7b1fa2", color: "#7b1fa2", fontWeight: 600 }}
                        />
                    </Tooltip>

                    <ToggleButtonGroup
                        value={filter}
                        exclusive
                        onChange={(_, v) => {
                            if (v) {
                                setFilter(v);
                                setSelectedIds(new Set());
                            }
                        }}
                        size="small"
                    >
                        <ToggleButton value="all">All</ToggleButton>
                        <ToggleButton value="not_billed">99453 Pending</ToggleButton>
                        <ToggleButton value="monthly_pending">99454/99457 Pending</ToggleButton>
                        <ToggleButton value="partial_pending">99445/99470 Pending</ToggleButton>
                    </ToggleButtonGroup>

                    <Button
                        variant="contained"
                        startIcon={<SendIcon />}
                        disabled={submitting || selectedIds.size === 0}
                        onClick={handlePreSubmit}
                        sx={{ backgroundColor: "#50869A", "&:hover": { backgroundColor: "#3e6e80" }, textTransform: "none", fontWeight: 600 }}
                    >
                        Submit 99453 ({selectedIds.size})
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<SendIcon />}
                        disabled={submitting || selectedIds.size === 0}
                        onClick={handlePreSubmitMonthly}
                        sx={{ backgroundColor: "#7b1fa2", "&:hover": { backgroundColor: "#6a1b9a" }, textTransform: "none", fontWeight: 600 }}
                    >
                        Submit Monthly CPTs ({selectedIds.size})
                    </Button>
                </div>

                <TableContainer component={Paper} sx={{ maxHeight: "calc(100vh - 250px)", boxShadow: 2 }}>
                    <Table stickyHeader size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell padding="checkbox">
                                    <Checkbox
                                        indeterminate={selectedIds.size > 0 && selectedIds.size < selectablePatients.length}
                                        checked={selectablePatients.length > 0 && selectedIds.size === selectablePatients.length}
                                        onChange={toggleSelectAll}
                                    />
                                </TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Patient Name</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Department</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>DOB</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>First BP</TableCell>
                                <TableCell sx={{ fontWeight: 700, textAlign: "center" }}>99453</TableCell>
                                <TableCell sx={{ fontWeight: 700, textAlign: "center" }}>99454</TableCell>
                                <TableCell sx={{ fontWeight: 700, textAlign: "center" }}>99457</TableCell>
                                <TableCell sx={{ fontWeight: 700, textAlign: "center" }}>99445</TableCell>
                                <TableCell sx={{ fontWeight: 700, textAlign: "center" }}>99470</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredPatients.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={10} align="center">
                                        {loading ? "Loading..." : "No patients found"}
                                    </TableCell>
                                </TableRow>
                            ) : (
                                filteredPatients.map((p) => {
                                    const claims = p.claims || {};
                                    const setup = claims["99453"] || {};
                                    const setupBilled = setup.billed;
                                    const eligible = isEligible(p);

                                    return (
                                        <TableRow key={getRowKey(p)} hover selected={selectedIds.has(getRowKey(p))}>
                                            <TableCell padding="checkbox">
                                                <Tooltip
                                                    title={setupBilled ? "Already billed" : !eligible ? "First BP older than 12 months" : ""}
                                                    arrow
                                                    placement="right"
                                                    disableHoverListener
                                                >
                                                    <span>
                                                        <Checkbox checked={selectedIds.has(getRowKey(p))} onChange={() => toggleSelect(getRowKey(p))} />
                                                    </span>
                                                </Tooltip>
                                            </TableCell>
                                            <TableCell>
                                                <span
                                                    onClick={() => handlePatientClick(p)}
                                                    style={{ cursor: "pointer", color: "#1565c0", fontWeight: 500, textDecoration: "underline dotted" }}
                                                >
                                                    {p.firstname} {p.lastname}
                                                </span>
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={getSourceLabel(p)}
                                                    size="small"
                                                    variant="outlined"
                                                    sx={{ fontWeight: 600, backgroundColor: p.sourcepracticeid === 1111 ? "#fff3e0" : "#f5f5f5" }}
                                                />
                                                {p.billing_ready === false && (
                                                    <Chip label="No Athena ID" size="small" sx={{ ml: 0.5, fontWeight: 600, backgroundColor: "#ffebee", color: "#b71c1c" }} />
                                                )}
                                            </TableCell>
                                            <TableCell>{p.dob}</TableCell>
                                            <TableCell sx={{ fontSize: "12px" }}>
                                                <span style={{ color: eligible ? "inherit" : "#d32f2f", fontWeight: eligible ? 400 : 600 }}>
                                                    {p.first_bp_date}
                                                </span>
                                                {!eligible && !setupBilled && (
                                                    <Tooltip title="Older than 12 months — not eligible for 99453" arrow>
                                                        <WarningAmberIcon sx={{ fontSize: 14, color: "#d32f2f", ml: 0.5, verticalAlign: "middle" }} />
                                                    </Tooltip>
                                                )}
                                            </TableCell>
                                            <TableCell align="center">
                                                <SetupDot claimInfo={setup} />
                                            </TableCell>
                                            <TableCell align="center">
                                                <BillingDots monthEntries={claims["99454"] || []} readingDays={p.reading_days || {}} eligibilityBand="full" windowFrom={windowFrom} windowTo={windowTo} />
                                            </TableCell>
                                            <TableCell align="center">
                                                <BillingDots monthEntries={claims["99457"] || []} readingDays={p.reading_days || {}} eligibilityBand="full" windowFrom={windowFrom} windowTo={windowTo} />
                                            </TableCell>
                                            <TableCell align="center">
                                                <BillingDots monthEntries={claims["99445"] || []} readingDays={p.reading_days || {}} eligibilityBand="partial" windowFrom={windowFrom} windowTo={windowTo} />
                                            </TableCell>
                                            <TableCell align="center">
                                                <BillingDots monthEntries={claims["99470"] || []} readingDays={p.reading_days || {}} eligibilityBand="partial" windowFrom={windowFrom} windowTo={windowTo} />
                                            </TableCell>
                                        </TableRow>
                                    );
                                })
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </SideNavLayout>

            <Dialog open={drillOpen} onClose={() => setDrillOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, fontWeight: 700, color: "#1d2951" }}>
                    <ReceiptLongIcon sx={{ color: "#50869A" }} />
                    {drillPatient ? `${drillPatient.firstname} ${drillPatient.lastname} — Reading History` : "Reading History"}
                </DialogTitle>
                <DialogContent dividers>
                    {drillLoading ? (
                        <div style={{ display: "flex", justifyContent: "center", padding: "40px" }}>
                            <CircularProgress sx={{ color: "#50869A" }} />
                        </div>
                    ) : drillData?.months?.length === 0 ? (
                        <Typography color="textSecondary" align="center" sx={{ py: 4 }}>
                            No readings found for this patient.
                        </Typography>
                    ) : (
                        <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                            {drillData?.months?.map((m) => {
                                const fullBilled = m.billed_99454 && m.billed_99457;
                                const partialBilled = m.billed_99445 && m.billed_99470;
                                const fullEligible = m.eligible;
                                const partialEligible = m.partial_eligible;
                                const allBilled = fullEligible ? fullBilled : partialEligible ? partialBilled : false;
                                const eligible = (fullEligible && !fullBilled) || (partialEligible && !partialBilled);
                                const isExpanded = expandedMonth === m.month_key;
                                const progressPct = Math.min((m.distinct_days / 16) * 100, 100);

                                return (
                                    <div
                                        key={m.month_key}
                                        style={{
                                            border: `2px solid ${allBilled ? "#4caf50" : eligible ? "#ff9800" : "#bdbdbd"}`,
                                            borderRadius: "8px",
                                            backgroundColor: allBilled ? "#f1f8e9" : eligible ? "#fff8e1" : "#fafafa",
                                            overflow: "hidden",
                                        }}
                                    >
                                        <div
                                            onClick={() => setExpandedMonth(isExpanded ? null : m.month_key)}
                                            style={{ display: "flex", justifyContent: "space-between", padding: "12px 16px", cursor: "pointer" }}
                                        >
                                            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                                                <Typography variant="subtitle1" fontWeight={700} sx={{ minWidth: 140 }}>
                                                    {m.month_label}
                                                </Typography>
                                                <Chip label={`${m.distinct_days} days`} size="small" />
                                                <Chip label={`${m.reading_count} readings`} size="small" variant="outlined" />
                                            </div>
                                            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: "12px" }}>
                                                {fullEligible
                                                    ? allBilled ? "99454/99457 billed" : "99454/99457 eligible"
                                                    : partialEligible
                                                      ? allBilled ? "99445/99470 billed" : "99445/99470 eligible"
                                                      : "Not eligible"}
                                            </Typography>
                                        </div>
                                        {isExpanded && (
                                            <div style={{ padding: "0 16px 12px" }}>
                                                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                                                    <Typography variant="caption" color="textSecondary">
                                                        Progress to 16 days:
                                                    </Typography>
                                                    <div style={{ flex: 1, height: 6, borderRadius: 3, backgroundColor: "#e0e0e0" }}>
                                                        <div
                                                            style={{
                                                                height: "100%",
                                                                borderRadius: 3,
                                                                width: `${progressPct}%`,
                                                                backgroundColor: m.distinct_days >= 16 ? "#4caf50" : "#ff9800",
                                                            }}
                                                        />
                                                    </div>
                                                    <Typography variant="caption" fontWeight={600}>
                                                        {m.distinct_days}/16
                                                    </Typography>
                                                </div>
                                                <Table size="small">
                                                    <TableHead>
                                                        <TableRow>
                                                            <TableCell>#</TableCell>
                                                            <TableCell>Date</TableCell>
                                                            <TableCell>Type</TableCell>
                                                            <TableCell>Values</TableCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {m.readings.map((r, ri) => (
                                                            <TableRow key={`${m.month_key}-${ri}`} hover>
                                                                <TableCell>{ri + 1}</TableCell>
                                                                <TableCell>{r.datetime}</TableCell>
                                                                <TableCell>{r.type}</TableCell>
                                                                <TableCell sx={{ fontFamily: "monospace" }}>{r.values}</TableCell>
                                                            </TableRow>
                                                        ))}
                                                    </TableBody>
                                                </Table>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDrillOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={confirmOpen} onClose={() => !submitting && setConfirmOpen(false)} maxWidth="xl" fullWidth>
                <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, fontWeight: 700, color: "#1d2951" }}>
                    <ReceiptLongIcon sx={{ color: "#50869A" }} />
                    {isSubmitDone ? "Submission Results" : submitting ? "Submitting Claims..." : "Review Claims Before Submission"}
                </DialogTitle>
                <DialogContent dividers>
                    {previewLoading && (
                        <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "24px", justifyContent: "center" }}>
                            <CircularProgress size={20} sx={{ color: "#50869A" }} />
                            <Typography variant="body2">Loading backend preview…</Typography>
                        </div>
                    )}

                    {!previewLoading && billingMode === "monthly" && claimsPreview.length > 0 && (
                        <div style={{ marginBottom: 12, padding: "10px 14px", background: "#e8f4f8", borderRadius: 6, border: "1px solid #50869A" }}>
                            <Typography variant="body2" fontWeight={700} sx={{ color: "#1d2951" }}>
                                {claimsPreview.length} Athena claim{claimsPreview.length === 1 ? "" : "s"} will be submitted across{" "}
                                {new Set(claimsPreview.map((c) => c.rowkey)).size} patient
                                {new Set(claimsPreview.map((c) => c.rowkey)).size === 1 ? "" : "s"}.
                            </Typography>
                        </div>
                    )}

                    {!previewLoading && billingMode === "monthly" && claimsPreview.length === 0 && (
                        <div style={{ padding: "24px", textAlign: "center" }}>
                            <Typography variant="body2" color="textSecondary">
                                Backend preview returned no billable months for the selected patients.
                            </Typography>
                        </div>
                    )}

                    {!previewLoading && backendNotes.length > 0 && (
                        <div style={{ marginBottom: 12, padding: "10px 14px", background: "#ffebee", borderRadius: 6, border: "1px solid #d32f2f" }}>
                            <Typography variant="body2" fontWeight={700} sx={{ color: "#b71c1c", mb: 0.5 }}>
                                Not submitted by the backend:
                            </Typography>
                            {backendNotes.map((n) => (
                                <Typography key={n.key} variant="caption" component="div" sx={{ color: "#5d4037" }}>
                                    {n.text}
                                </Typography>
                            ))}
                        </div>
                    )}

                    {!previewLoading && driftWarnings.length > 0 && (
                        <div style={{ marginBottom: 12, padding: "10px 14px", background: "#fff8e1", borderRadius: 6, border: "1px solid #ff9800" }}>
                            <Typography variant="body2" fontWeight={700} sx={{ color: "#e65100", mb: 0.5 }}>
                                Eligibility drift between UI and backend:
                            </Typography>
                            {driftWarnings.map((d) => (
                                <Typography key={d.rowkey} variant="caption" component="div" sx={{ color: "#5d4037" }}>
                                    {d.name} — UI expected {d.frontend}, backend will submit {d.backend}
                                </Typography>
                            ))}
                        </div>
                    )}

                    {submitting && (
                        <LinearProgress
                            variant="determinate"
                            value={(submitProgress / Math.max(1, new Set(claimsPreview.map((c) => c.rowkey)).size)) * 100}
                            sx={{ mb: 2, height: 6, borderRadius: 3, "& .MuiLinearProgress-bar": { backgroundColor: "#50869A" } }}
                        />
                    )}

                    {!previewLoading && claimsPreview.length > 0 && (
                        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
                            <Table size="small" stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>#</TableCell>
                                        <TableCell>Patient</TableCell>
                                        <TableCell>Source</TableCell>
                                        <TableCell>Patient ID</TableCell>
                                        <TableCell>Dept</TableCell>
                                        <TableCell>CPT</TableCell>
                                        {billingMode === "monthly" ? <TableCell>Month</TableCell> : <TableCell>DOS</TableCell>}
                                        {billingMode === "monthly" && <TableCell>Days</TableCell>}
                                        <TableCell>Insurance</TableCell>
                                        <TableCell>Status</TableCell>
                                        <TableCell>Error</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {claimsPreview.map((claim, index) => (
                                        <TableRow key={`${claim.rowkey}-${claim.month || claim.dos}-${claim.cpt}`}>
                                            <TableCell>{index + 1}</TableCell>
                                            <TableCell>{claim.name}</TableCell>
                                            <TableCell>{claim.sourceLabel || `Dept ${claim.departmentid}`}</TableCell>
                                            <TableCell>{claim.patientid}</TableCell>
                                            <TableCell>{claim.departmentid}</TableCell>
                                            <TableCell>{claim.cpt}</TableCell>
                                            {billingMode === "monthly" ? <TableCell>{claim.month}</TableCell> : <TableCell>{claim.dos}</TableCell>}
                                            {billingMode === "monthly" && <TableCell>{claim.days}</TableCell>}
                                            <TableCell>{claim.insuranceUsed}</TableCell>
                                            <TableCell>
                                                <StatusIcon status={claim.status} error={claim.error} />
                                            </TableCell>
                                            <TableCell sx={{ maxWidth: 260, fontSize: "12px", color: "#d32f2f" }}>{claim.error || "—"}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmOpen(false)} disabled={submitting}>
                        {isSubmitDone ? "Close" : "Cancel"}
                    </Button>
                    {!isSubmitDone && (
                        <Button
                            variant="contained"
                            startIcon={<SendIcon />}
                            disabled={submitting || previewLoading || claimsPreview.length === 0}
                            onClick={billingMode === "monthly" ? handleConfirmedSubmitMonthly : handleConfirmedSubmit}
                            sx={{ backgroundColor: "#50869A", "&:hover": { backgroundColor: "#3e6e80" }, textTransform: "none", fontWeight: 600 }}
                        >
                            {submitting ? "Submitting..." : "Confirm Submit"}
                        </Button>
                    )}
                </DialogActions>
            </Dialog>

            <Backdrop open={loading || submitting} sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}>
                <CircularProgress color="inherit" />
            </Backdrop>

            <ToastMessage open={openToast} message={toastMessage} severity={toastSeverity} handleClose={() => setOpenToast(false)} />
        </div>
    );
};

export default RpmBilling;
